// キャラを変える操作（2026-10-23 に画面のモジュール parts・tools・pointer・partbox・palette・colors・random・name・history から集めた）。
// どれも C.state（と C.name）を書きかえて、描き直す（render か place）だけ。画面の部品には触らない。
// まとめて付けるカテゴリ（色の連動・グループ）は画面が決めて targets で渡す（新しい画面は使う人が選ぶ連動、クラシック版はデータの GROUP）
import { C, blankLine, catByKey } from './model.js';
import { keyOf } from './util.js';
import { canPattern } from './pattern.js';
import { applyZoom, place, render, renderAll } from './render.js';
import { emit } from './events.js';
import { ZOOM_MAX, ZOOM_STEP } from '../config.js';
import type { Category, Part, PartState } from '../types.js';

const round2 = (v: number) => Math.round(v * 100) / 100;

// ---- 左右でひと組（2026-10-27〜、DRESSUP2 の め・くつした・ガーター。画面の merge.ts が「両方」のあいだだけ入れる） ----
// 左のキー → 右のキー。ここにあるカテゴリへの操作は、右にも同じに掛ける（パーツ・反転・うすさは同じ値、大きさは同じだけ、
// 横の位置と回転は左右対称に＝反対の向きへ同じだけ）。右にまだ何も無ければ、先に左の行を左右対称にして写す
export const links: Record<string, string> = {};
function partner(cat: Category): Category | null {
  const k = links[keyOf(cat)];
  return (k && catByKey[k]) || null;
}
function mirrored(st: PartState): PartState {
  const line: PartState = JSON.parse(JSON.stringify(st));
  line.tx = -(Number(st.tx) || 0);
  line.rotate = -(Number(st.rotate) || 0);
  return line;
}
// 右の行（無ければ左から写す。左にも無ければ null）
function partnerLine(cat: Category, p: Category): PartState | null {
  const pk = keyOf(p);
  if (!C.state[pk] && C.state[keyOf(cat)]) C.state[pk] = mirrored(C.state[keyOf(cat)]);
  return C.state[pk] || null;
}
// 色・柄などの「まとめて付けるカテゴリ」に右を足す
function withPartners(targets: Category[]): Category[] {
  const out = targets.slice();
  targets.forEach((c) => { const p = partner(c); if (p && out.indexOf(p) < 0) out.push(p); });
  return out;
}

// パーツを付ける。選んでいるパーツをもう一度選ぶと先頭（なし）に戻る（エンジンと同じ）。
// はじめて付けるカテゴリは、左右反転のカテゴリ（REVERSE = 1。持ちもの（右））なら反転した向きで始める
// （本物はパーツを替えるたびに反転を REVERSE == 1 に戻す。新しい画面は反転したパーツを替えても反転を残すので、はじめて付けるときだけ）
export function setPart(cat: Category, part: Part, list: Part[]): { st: PartState; fresh: boolean } {
  const key = keyOf(cat);
  let st = C.state[key];
  const fresh = !st;
  if (!st) {
    st = C.state[key] = blankLine();
    if (cat.REVERSE == 1) st.rev = 1;
  }
  st.parts = st.parts === part.id ? list[0].id : part.id;
  render(key, 'part');
  const p = partner(cat);
  if (p) {
    const pk = keyOf(p), had = !!C.state[pk];
    const line = partnerLine(cat, p)!;
    if (had) line.parts = st.parts;
    render(pk, 'part');
  }
  return { st, fresh };
}
// 左右反転
export function toggleRev(cat: Category) {
  const st = C.state[keyOf(cat)];
  st.rev = Number(st.rev) === 1 ? 0 : 1;
  place(cat, st, 'rev');
  const p = partner(cat), line = p && partnerLine(cat, p);
  if (p && line) { line.rev = st.rev; place(p, line, 'rev'); }
}
// 大きく（dir 1）・小さく（-1）。1 ± ZOOM_MAX まで ZOOM_STEP ずつ（btnSubExpand と同じ）
export function zoomStep(cat: Category, dir: number) {
  const key = keyOf(cat), st = C.state[key];
  const step = (s: PartState) => { s.scale = dir > 0 ? round2(Math.min(1 + ZOOM_MAX, Number(s.scale) + ZOOM_STEP)) : round2(Math.max(1 - ZOOM_MAX, Number(s.scale) - ZOOM_STEP)); };
  step(st);
  render(key, 'zoom');
  const p = partner(cat), line = p && partnerLine(cat, p);
  if (p && line) { step(line); render(keyOf(p), 'zoom'); }
}
// 位置・大きさ・回転を元に戻す
export function resetTransform(cat: Category) {
  const key = keyOf(cat), st = C.state[key];
  st.tx = 0; st.ty = 0; st.scale = 1; st.rotate = 0;
  render(key, 'reset');
  const p = partner(cat), line = p && partnerLine(cat, p);
  if (p && line) { line.tx = 0; line.ty = 0; line.scale = 1; line.rotate = 0; render(keyOf(p), 'reset'); }
}
// うすさ（percent: 100 で不透明）
export function setOpacity(cat: Category, percent: number) {
  const st = C.state[keyOf(cat)];
  st.opacity = percent >= 100 ? 1 : percent / 100;
  place(cat, st, 'opacity');
  const p = partner(cat), line = p && partnerLine(cat, p);
  if (p && line) { line.opacity = st.opacity; place(p, line, 'opacity'); }
}
// 1 ずつなど、今の位置からずらす（0 の向きは書きかえない）
export function moveBy(cat: Category, dx: number, dy: number) {
  const st = C.state[keyOf(cat)];
  if (dx) st.tx = Number(st.tx) + dx;
  if (dy) st.ty = Number(st.ty) + dy;
  place(cat, st, 'move');
  const p = partner(cat), line = p && partnerLine(cat, p);
  if (p && line) {
    if (dx) line.tx = Number(line.tx) - dx;
    if (dy) line.ty = Number(line.ty) + dy;
    place(p, line, 'move');
  }
}
// 位置を決める（ドラッグ）。tx が null なら横は書きかえない（縦だけ動かせるカテゴリ）
export function setOffset(cat: Category, tx: number | null, ty: number) {
  const st = C.state[keyOf(cat)];
  const dx = tx !== null ? tx - (Number(st.tx) || 0) : 0, dy = ty - (Number(st.ty) || 0);
  if (tx !== null) st.tx = tx;
  st.ty = ty;
  place(cat, st, 'drag');
  const p = partner(cat), line = p && partnerLine(cat, p);
  if (p && line) {
    if (tx !== null) line.tx = (Number(line.tx) || 0) - dx;
    line.ty = (Number(line.ty) || 0) + dy;
    place(p, line, 'drag');
  }
}
// 回転（度。保存データの向き）
export function setRotate(cat: Category, deg: number) {
  const st = C.state[keyOf(cat)];
  const d = deg - (Number(st.rotate) || 0);
  st.rotate = deg;
  place(cat, st, 'rotate');
  const p = partner(cat), line = p && partnerLine(cat, p);
  if (p && line) { line.rotate = (Number(line.rotate) || 0) - d; place(p, line, 'rotate'); }
}
// 大きさを決める（枠のつまみのドラッグ）。tx・ty は null なら書きかえない。パーツは入れ直さず拡大だけ付け直す
export function setScale(cat: Category, z: number, tx: number | null, ty: number | null) {
  const st = C.state[keyOf(cat)];
  const dz = z - (Number(st.scale) || 1), dx = tx !== null ? tx - (Number(st.tx) || 0) : 0, dy = ty !== null ? ty - (Number(st.ty) || 0) : 0;
  st.scale = z;
  if (tx !== null) st.tx = tx;
  if (ty !== null) st.ty = ty;
  applyZoom(cat, st, 'drag');
  place(cat, st, 'drag');
  const p = partner(cat), line = p && partnerLine(cat, p);
  if (p && line) {
    line.scale = round2((Number(line.scale) || 1) + dz);
    line.tx = (Number(line.tx) || 0) - dx;
    line.ty = (Number(line.ty) || 0) + dy;
    applyZoom(p, line, 'drag');
    place(p, line, 'drag');
  }
}
// 柄を付ける・外す（本物の setPattern・clearPattern と同じく、初めて付けるときはそのときの色反転 prev を ptnmode に、外すと両方 0）
export function setPattern(targets: Category[], id: string) {
  withPartners(targets).forEach((c) => {
    const key = keyOf(c), st = C.state[key];
    if (!st || !canPattern(c)) return;
    if (id && !st.ptn) st.ptnmode = st.prev || 0;
    st.ptn = id;
    if (!id) { st.ptnmode = 0; st.prev = 0; }
    render(key, 'pattern');
  });
}
// 柄の色の入れかえ（prev 0 ⇔ 1。柄の付いているカテゴリだけ）
export function setPatternSwap(targets: Category[], prev: number) {
  withPartners(targets).forEach((c) => {
    const key = keyOf(c), st = C.state[key];
    if (!st || !canPattern(c) || !st.ptn) return;
    st.prev = prev;
    render(key, 'pattern');
  });
}
// 色（両方に）。左・右だけの色は消える（本物と同じ）
export function setColor(targets: Category[], hex: string) {
  withPartners(targets).forEach((c) => {
    const key = keyOf(c), st = C.state[key];
    if (!st || !c.COLOR.length) return;
    st.col = hex;
    st.lfrom = st.lto = st.rfrom = st.rto = '';
    render(key, 'color');
  });
}
// 左（side 1）・右（2）だけの色。本物は全体の色（col）があるときだけ左右の色を読み直すので、無ければ元の色を入れておく（見た目は同じ）。
// hex が '' なら元の色
export function setSideColor(targets: Category[], side: number, hex: string) {
  withPartners(targets).forEach((c) => {
    const key = keyOf(c), st = C.state[key];
    if (!st || !c.COLOR.length) return;
    const v = hex || c.COLOR[0].toLowerCase();
    if (!st.col) st.col = c.COLOR[0].toLowerCase();
    if (side === 1) { st.lfrom = v; st.lto = v; } else { st.rfrom = v; st.rto = v; }
    render(key, 'color');
  });
}
// グラデーションの上・下の色（グラデーションのカテゴリだけ。chibi.setAllGradColor と同じ）
export function setGrad(targets: Category[], from: string, to: string) {
  withPartners(targets).forEach((c) => {
    const key = keyOf(c), st = C.state[key];
    if (!st || !(c.GRAD.length > 1)) return;
    st.from = from; st.to = to;
    render(key, 'color');
  });
}
// 名前（描くのは画面）
export function setName(name: string, color: string) {
  C.name = [name, color];
}
// 1つ目と2つ目の置き場所（重ね付け）の中身を入れかえる（パーツ・色・柄・位置など、保存データの1行ぶん。重なりの上下が入れかわる）
export function swapSlots(keyA: string, keyB: string) {
  const a = C.state[keyA], b = C.state[keyB];
  if (b) C.state[keyA] = b; else delete C.state[keyA];
  if (a) C.state[keyB] = a; else delete C.state[keyB];
  render(keyA, 'part');
  render(keyB, 'part');
}
// 全部を入れかえて描き直す（ランダム）
export function replaceState(next: Record<string, PartState>) {
  C.state = next;
  renderAll('random');
}
// 前の形に戻す（元に戻す・やり直す）。state・name はそのまま使うので、写しを渡す
export function restoreState(state: Record<string, PartState>, name: [string, string]) {
  C.state = state;
  C.name = name;
  renderAll('undo');
  emit('applied');
}

// ---- クラシック版の操作（2026-10-23〜、段階 B3） ----
// 前のエンジン（chibi.js・charat.common.js の changeParts・reverseG・zoomG・moveG・selGrad・setAllGradColor）の決まりのまま、状態を書きかえるだけ。
// 描くのと、クラシック版の画面が読むレイヤーの属性（param*）を書くのは classic.ts（前のエンジンと同じ手順で、変わる所だけ）。
// パーツの無いカテゴリにも行を作る（前のエンジンはパーツが無くても位置・反転・色の属性を持っていて、あとでパーツを付けるとそれが効くため。
// parts が null の行は保存データに書かない・描かない）
export function lineOf(cat: Category): PartState {
  const key = keyOf(cat);
  return C.state[key] || (C.state[key] = blankLine());
}
// パーツを替える（changeParts）。resetMove: 位置・回転を 0 に（KEEP_MOVE_ON_PARTS_CHANGE でないメーカーで、先頭のレイヤーにパーツの層があるとき。
// 前のエンジンは層のあるレイヤーの属性だけ 0 にし、保存データは先頭のレイヤーから書く）。resetScale: 拡大を 1 に（差しかえの動き 1）
export function classicPart(cat: Category, id: number, resetMove: boolean, resetScale: boolean) {
  const st = lineOf(cat);
  st.parts = id;
  if (resetMove) { st.tx = 0; st.ty = 0; st.rotate = 0; }
  if (resetScale) st.scale = 1;
  return st;
}
// 反転する・戻す（reverseG）
export function classicRev(cat: Category, on: boolean) {
  const st = lineOf(cat);
  st.rev = on ? 1 : 0;
  return st;
}
// 拡大を決める（zoomG。次の倍率は charat.draw.js の nextScale で classic.ts が決める）
export function classicZoom(cat: Category, scale: number) {
  const st = lineOf(cat);
  st.scale = scale;
  return st;
}
// ずらす（moveG）。両方 0 なら位置を 0 に戻す
export function classicMove(cat: Category, dx: number, dy: number) {
  const st = lineOf(cat);
  if (dx === 0 && dy === 0) { st.tx = 0; st.ty = 0; } else { st.tx = (Number(st.tx) || 0) + dx; st.ty = (Number(st.ty) || 0) + dy; }
  return st;
}
// 色（selGrad の色の所。side 0 全体・1 左・2 右）。全体なら col・from・to を同じ色にして左右の色を消す（新しい画面の setColor と違い、from・to も入れる）
export function classicColor(cat: Category, hex: string, side: number) {
  const st = lineOf(cat);
  if (side === 1) { st.lfrom = hex; st.lto = hex; } else if (side === 2) { st.rfrom = hex; st.rto = hex; } else {
    st.col = hex; st.from = hex; st.to = hex;
    st.lfrom = st.lto = st.rfrom = st.rto = '';
  }
  return st;
}
// 柄を付ける（setPattern、段階 B4）: 選んでいるカテゴリに柄の番号を入れ、はじめて付けるとき（柄が空）はそのときの色の入れかえを柄の入れかえ（ptnmode）に。
// group: 柄が一覧にあったときに同じ柄・入れかえを入れるカテゴリ（エンジンの changePattern。選んでいるカテゴリの柄の defs をグループで使う）
export function classicSetPattern(sel: Category, group: Category[], id: string) {
  const st = lineOf(sel);
  if (!st.ptn) st.ptnmode = st.prev;
  st.ptn = id;
  group.forEach((cat) => {
    const line = lineOf(cat);
    line.ptn = id;
    line.ptnmode = st.ptnmode;
    line.prev = st.prev;
  });
  return st;
}
// 柄を外す（clearPattern）: 選んでいるカテゴリと group の柄・柄の入れかえ・色の入れかえを空に
export function classicClearPattern(sel: Category, group: Category[]) {
  [sel].concat(group).forEach((cat) => {
    const line = lineOf(cat);
    line.ptn = '';
    line.ptnmode = 0;
    line.prev = 0;
  });
}
// パーツ番号をレイヤーの属性に合わせる（段階 B5。クラシック版のメーカー専用の後処理＝手・髪型の排他が、エンジンの外でレイヤーの paramParts を書きかえるため）。
// id が null ならパーツなし
export function classicSyncParts(cat: Category, id: number | null) {
  lineOf(cat).parts = id;
}
// 柄・色の入れかえの値をレイヤーの属性に合わせる（第1世代の柄＝mono は、柄の操作をエンジンの画面ファイルがする）
export function classicSyncPattern(cat: Category, ptn: string, prev: number, ptnmode: number) {
  const st = lineOf(cat);
  st.ptn = ptn;
  st.prev = prev;
  st.ptnmode = ptnmode;
}
// 回転（rotateByDelta、段階 B5）・うすさ（OpacityG）の値。足し方（反転中は向きを逆に）・描くのは classic.ts（前のエンジンと同じ手順）
export function classicRotate(cat: Category, rotate: number) {
  lineOf(cat).rotate = rotate;
  return lineOf(cat);
}
export function classicOpacity(cat: Category, opacity: number) {
  lineOf(cat).opacity = opacity;
  return lineOf(cat);
}
// 色の入れかえの値（btnRevColor。進め方はエンジンと同じで classic.ts）
export function classicPrev(cat: Category, prev: number) {
  lineOf(cat).prev = prev;
}
// グラデーションの上・下（setAllGradColor）。左・右を選んでいれば、その側の色にも同じ上・下を
export function classicGrad(cat: Category, from: string, to: string, side: number) {
  const st = lineOf(cat);
  st.from = from; st.to = to;
  if (side === 1) { st.lfrom = from; st.lto = to; } else if (side === 2) { st.rfrom = from; st.rto = to; }
  return st;
}
